import {RcfaComplianceTableData, sortPlantsByComplianceScoreBar} from '@domain/rcfa'
import {useSorting} from '@hconnect/common/hooks'
import {customThemeConstants, expandableTableHeaderCellSx} from '@hconnect/uikit/src/lib2'
import {
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Table,
  TableSortLabel,
  SortDirection,
  Theme,
  Box
} from '@mui/material'
import {SxProps} from '@mui/system'
import {orderBy, isEqual} from 'lodash'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {getCompletedMandatoryRcfasText} from '../utils'

import {HeaderWithTooltip} from './HeaderWithTooltip'
import {
  RCFA_COMPLIANCE_TABLE_KEYS,
  RcfaComplianceTableKey,
  renderRcfaComplianceTableCell,
  RcfaComplianceTableKeys
} from './RcfaComplianceTableRow'

type PlantTableProps = {
  data: RcfaComplianceTableData[]
  onPlantItemClick: (plantId: string) => void
}

type PlantTableHeaderCellProps = {
  columnKey: RcfaComplianceTableKey
  orderByKey?: RcfaComplianceTableKey
  sortDir: SortDirection
  onSortClick: (key: RcfaComplianceTableKey) => void
  sx?: SxProps<Theme>
  children?: React.ReactNode
}

const SortableHeaderCell = ({
  columnKey,
  orderByKey,
  sortDir,
  onSortClick,
  children,
  sx
}: PlantTableHeaderCellProps) => (
  <TableCell sortDirection={orderByKey === columnKey && sortDir} sx={sx}>
    <TableSortLabel
      active={sortDir && orderByKey === columnKey}
      direction={orderByKey === columnKey && sortDir ? sortDir : undefined}
      onClick={() => onSortClick(columnKey)}
    >
      {children}
    </TableSortLabel>
  </TableCell>
)

const sortPlants = (
  plants: RcfaComplianceTableData[],
  orderByKey: RcfaComplianceTableKey,
  sortDir: SortDirection
): RcfaComplianceTableData[] => {
  if (orderByKey === RcfaComplianceTableKeys.COMPLIANCE_SCORE_BAR) {
    return sortPlantsByComplianceScoreBar(plants, sortDir)
  }
  if (orderByKey === RcfaComplianceTableKeys.LOCATION) {
    return orderBy(plants, ['name'], [sortDir])
  }
  if (orderByKey === RcfaComplianceTableKeys.UNRESOLVED_TASKS) {
    return orderBy(plants, ['unresolvedTasksCount'], [sortDir])
  }
  if (orderByKey === RcfaComplianceTableKeys.OVERDUE_TASKS) {
    return orderBy(plants, ['overdueTasksCount'], [sortDir])
  }
  return orderBy(plants, [orderByKey], [sortDir])
}

const RcfaCompliancePlantListComponent: React.FC<PlantTableProps> = ({data, onPlantItemClick}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const {sortedList, onSortClick, orderByKey, sortDir} = useSorting<
    RcfaComplianceTableData,
    RcfaComplianceTableKey
  >({
    initialSortDir: false,
    initialOrderByKey: RcfaComplianceTableKeys.LOCATION,
    data,
    sort: sortPlants
  })

  return (
    <TableContainer data-test-id="rcfa-compliance-plants-table">
      <Table>
        <TableHead>
          <TableRow>
            <SortableHeaderCell
              columnKey={RcfaComplianceTableKeys.LOCATION}
              orderByKey={orderByKey}
              sortDir={sortDir}
              onSortClick={onSortClick}
            >
              <Box sx={expandableTableHeaderCellSx(true)}>{t('label.location')}</Box>
            </SortableHeaderCell>
            <SortableHeaderCell
              columnKey={RcfaComplianceTableKeys.COMPLIANCE_SCORE_BAR}
              orderByKey={orderByKey}
              sortDir={sortDir}
              onSortClick={onSortClick}
            >
              <HeaderWithTooltip
                headerText={getCompletedMandatoryRcfasText(t)}
                tooltipText={t('tooltip.rcfaScore')}
              />
            </SortableHeaderCell>
            <TableCell />
            <SortableHeaderCell
              columnKey={RcfaComplianceTableKeys.UNRESOLVED_TASKS}
              orderByKey={orderByKey}
              sortDir={sortDir}
              onSortClick={onSortClick}
              sx={{textAlign: 'right'}}
            >
              <HeaderWithTooltip
                headerText={t('label.unresolvedTasks')}
                tooltipText={t('tooltip.unresolvedTasks')}
              />
            </SortableHeaderCell>
            <SortableHeaderCell
              columnKey={RcfaComplianceTableKeys.OVERDUE_TASKS}
              orderByKey={orderByKey}
              sortDir={sortDir}
              onSortClick={onSortClick}
              sx={{textAlign: 'right'}}
            >
              <HeaderWithTooltip
                headerText={t('label.overdueTasks')}
                tooltipText={t('tooltip.overdueTasks')}
              />
            </SortableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedList.map((plant, index) => (
            <TableRow
              key={plant.id}
              sx={{
                '&:hover': {
                  cursor: 'pointer',
                  backgroundColor: customThemeConstants().palette.primaryBackgroundColor
                }
              }}
              onClick={() => {
                onPlantItemClick(plant.id)
              }}
              data-test-id={`plant-table-row-${plant.id}`}
            >
              {RCFA_COMPLIANCE_TABLE_KEYS.map((key) => (
                <TableCell key={key}>{renderRcfaComplianceTableCell(plant, index, key)}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export const RcfaCompliancePlantList = React.memo(RcfaCompliancePlantListComponent, isEqual)
